// Recuperación automática cuando falla la carga de un chunk tras un deploy.
//
// El caso real que motivó esto (Ecuador UAT, 17-ago-2026): durante el despliegue el App Service
// reemplaza los archivos de `wwwroot` con el sitio andando, así que hay una ventana en la que
// `/_next/static/chunks/*.js` responde 404 con `content-type: text/plain`. El navegador entonces
// rechaza el script («Refused to execute script … because its MIME type ('text/plain') is not
// executable») y el import dinámico falla. Verificado después: el mismo chunk responde 200, o sea
// el 404 era de la ventana de deploy y no un archivo faltante.
//
// Un reload completo trae el HTML nuevo con el manifest correcto y la app se recupera. Y ES la única
// salida: el runtime de Turbopack **cachea la promesa rechazada** del chunk
// (`instrumentedBackendLoadChunks.set(thenable, entry)`), así que reintentar el mismo import
// devuelve el mismo rechazo incluso después de que el archivo vuelve a estar disponible.

export const INTENTOS_KEY = 'chunk-error-intentos'

// Dos recargas por incidente. Más que eso no aporta: si dos HTML nuevos seguidos siguen pidiendo
// chunks que no están, el problema no se arregla recargando y hay que decírselo al usuario.
export const MAX_INTENTOS = 2

// Evita disparos múltiples dentro del mismo ciclo de vida de la página
// (el boundary, unhandledrejection y el listener de recursos pueden ver el mismo fallo)
let reloadEnCurso = false

export const isChunkLoadError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false
  }
  const { name, message } = error as { name?: string; message?: string }
  const texto = message ?? ''

  return (
    // webpack
    name === 'ChunkLoadError' ||
    /loading chunk .* failed|failed to load chunk|loading css chunk/i.test(texto) ||
    // Turbopack (el bundler que usa este proyecto) lanza un Error común, sin `name` propio:
    // `Failed to load chunk <url> from a dynamic import`. Lo cubre `failed to load chunk`.
    // ESM nativo y el rechazo del navegador por MIME, que no traen ninguna de las cadenas de arriba
    /failed to fetch dynamically imported module|error loading dynamically imported module/i.test(texto) ||
    /importing a module script failed|refused to execute script/i.test(texto) ||
    // Último recurso: cualquier fallo que mencione un asset del build
    texto.includes('/_next/static/')
  )
}

const leerIntentos = (): number => {
  try {
    return Number(window.sessionStorage.getItem(INTENTOS_KEY) ?? 0) || 0
  } catch {
    return 0
  }
}

/**
 * Olvida el presupuesto de recargas. Lo llama `ChunkErrorRecovery` cuando la app lleva un rato
 * andando sin fallar, para que un incidente futuro vuelva a tener sus dos intentos.
 */
export const olvidarIntentosDeRecuperacion = (): void => {
  try {
    window.sessionStorage.removeItem(INTENTOS_KEY)
  } catch {
    // sessionStorage no disponible: no hay nada que olvidar
  }
}

/**
 * Recarga la página si todavía queda presupuesto. Devuelve `false` cuando ya se agotó, y ahí el
 * llamador debe mostrarle algo accionable al usuario en vez de un spinner.
 *
 * El guard cuenta INTENTOS y no una ventana de tiempo. La versión anterior permitía una recarga cada
 * 60 s, y durante un deploy cada ciclo de recarga tarda más que eso —el sitio se está reiniciando—,
 * así que la ventana ya había expirado en cada vuelta y el guard no acotaba nada: la app recargaba
 * en bucle mostrando «Hay una nueva versión disponible, actualizando…» sin llegar nunca a la rama
 * que registra el error. Que en Application Insights no haya NI UNA excepción de chunk en 24 h,
 * teniendo el reporte del usuario, es justamente la huella de eso.
 */
export const recoverFromChunkError = (): boolean => {
  if (typeof window === 'undefined' || reloadEnCurso) {
    return false
  }

  const intentos = leerIntentos()

  if (intentos >= MAX_INTENTOS) {
    return false
  }

  try {
    window.sessionStorage.setItem(INTENTOS_KEY, String(intentos + 1))
  } catch {
    // sessionStorage no disponible: el flag en memoria evita el bucle dentro de esta página
  }

  reloadEnCurso = true
  window.location.reload()

  return true
}
